import { useEffect } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { useAppDependencies } from "../../context/AppContext"
import PathRouter from "src/navigation/PathRouter"
import StateConstants from "src/states/StateContstants"

interface OldUrlRedirectProps {
    // No props needed - uses context
}

/**
 * Handles redirects from old URL patterns to new React Router routes.
 * 
 * Old patterns:
 * - /?path=/valuation&property_id=XXX&house_condition=YYY&removed_homes=ZZZ
 *   → /valuate/XXX/:addressSlug?house_condition=YYY&removed_homes=ZZZ
 * 
 * - /?path=/login → /login
 * - /?path=/contact → /contact
 * - /?path=/terms → /terms
 * - /?path=/saved-valuations → /saved-valuations
 */
export default function OldUrlRedirect(_props: OldUrlRedirectProps) {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const deps = useAppDependencies()

    useEffect(() => {
        const path = searchParams.get('path')
        const propertyId = searchParams.get(PathRouter.PROPERTY_ID_PARAM)
        const houseCondition = searchParams.get(PathRouter.HOUSE_CONDITION_PARAM)
        const removedHomes = searchParams.get(PathRouter.REMOVED_HOMES_PARAM)
        const address = searchParams.get(PathRouter.ADDRESS_PARAM)

        // Handle old valuation URL pattern
        if (path === PathRouter.VALUATION_PATH && propertyId) {
            const redirectToNewValuationUrl = async () => {
                try {
                    const addressResponse = await deps.apiHandler.getAddress({ "id": propertyId })
                    const addressSlug = addressResponse?.aurl || addressResponse?.address_url || ""
                    
                    if (addressSlug) {
                        let newUrl = `/valuate/${propertyId}/${encodeURIComponent(addressSlug)}`
                        const queryParams = new URLSearchParams()
                        
                        if (houseCondition) {
                            queryParams.set(PathRouter.HOUSE_CONDITION_PARAM, houseCondition)
                        }
                        if (removedHomes) {
                            queryParams.set(PathRouter.REMOVED_HOMES_PARAM, removedHomes)
                        }
                        
                        const queryString = queryParams.toString()
                        if (queryString) {
                            newUrl += `?${queryString}`
                        }
                        
                        navigate(newUrl, { replace: true })
                    } else {
                        // Fallback if we can't get address slug
                        navigate(`/valuate/${propertyId}`, { replace: true })
                    }
                } catch (error) {
                    console.error("Error redirecting valuation URL:", error)
                    deps.stateHandler.set(StateConstants.ERROR, error)
                    // Fallback to new route without slug
                    navigate(`/valuate/${propertyId}`, { replace: true })
                }
            }
            redirectToNewValuationUrl()
            return
        }

        // Handle other old URL patterns
        if (path === PathRouter.LOGIN_PATH) {
            navigate('/login', { replace: true })
            return
        }

        if (path === PathRouter.CONTACT_US_PATH) {
            navigate('/contact', { replace: true })
            return
        }

        if (path === PathRouter.TERMS_OF_USE_PATH) {
            navigate('/terms', { replace: true })
            return
        }

        if (path === PathRouter.SAVED_VALUATIONS_PATH) {
            navigate('/saved-valuations', { replace: true })
            return
        }

        // If path is home or unknown, redirect to home (clearing query params)
        if (!path || path === PathRouter.HOME_PATH) {
            navigate('/', { replace: true })
            return
        }

        // Unknown path, redirect to home
        navigate('/', { replace: true })
    }, [searchParams, navigate, deps])

    return deps.loader.render("Redirecting...")
}

